"use client";

import { useRef, useState } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";

interface CaseStudyGalleryProps {
    images: string[];
}

export function CaseStudyGallery({ images }: CaseStudyGalleryProps) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const slideRef = useRef<HTMLDivElement>(null);

    const nextSlide = () => {
        setCurrentIndex((prev) => (prev + 1) % images.length);
    };

    const prevSlide = () => {
        setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    useGSAP(() => {
        if (slideRef.current) {
            gsap.fromTo(slideRef.current,
                { opacity: 0, x: 20 },
                { opacity: 1, x: 0, duration: 0.5, ease: "power2.out" }
            );
        }
    }, [currentIndex]);

    if (!images || images.length === 0) return null;

    return (
        <div className="relative w-[60%] rounded-3xl overflow-hidden shadow-xl bg-gray-900 my-16 group mx-auto align-center justify-center" ref={containerRef}>
            {/* Main Image Display */}
            <div className="relative aspect-video w-full overflow-hidden">
                <div ref={slideRef} key={currentIndex} className="w-full h-full relative">
                    <Image
                        src={images[currentIndex]}
                        alt={`Project Gallery Image ${currentIndex + 1}`}
                        fill
                        className="object-cover"
                        unoptimized
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60"></div>
                </div>

                {/* Navigation Controls */}
                <button
                    onClick={prevSlide}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white hover:bg-white hover:text-gray-900 transition-all opacity-0 group-hover:opacity-100 transform -translate-x-4 group-hover:translate-x-0"
                >
                    <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                    onClick={nextSlide}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white hover:bg-white hover:text-gray-900 transition-all opacity-0 group-hover:opacity-100 transform translate-x-4 group-hover:translate-x-0"
                >
                    <ChevronRight className="w-6 h-6" />
                </button>

                {/* Counter Badge */}
                <div className="absolute bottom-6 right-6 bg-black/50 backdrop-blur-md px-4 py-1.5 rounded-full text-white text-sm font-medium border border-white/10">
                    {currentIndex + 1} / {images.length}
                </div>
            </div>

            {/* Thumbnails Strip */}
            <div className="bg-gray-900 p-4 flex gap-3 overflow-x-auto scrollbar-hide">
                {images.map((img, idx) => (
                    <button
                        key={idx}
                        onClick={() => setCurrentIndex(idx)}
                        className={`relative w-24 h-16 rounded-lg overflow-hidden flex-shrink-0 transition-all ${idx === currentIndex
                            ? "ring-2 ring-blue-500 opacity-100 scale-105"
                            : "opacity-50 hover:opacity-80"
                            }`}
                    >
                        <Image
                            src={img}
                            alt={`Thumbnail ${idx + 1}`}
                            fill
                            className="object-cover"
                            unoptimized
                        />
                    </button>
                ))}
            </div>
        </div>
    );
}
